import { gql } from "@apollo/client";
import { useMutation } from "@apollo/client";

import { ConsumerApiError } from "../custom-events";
import { BookingConsumerTripInput } from "../types/ProductFullLoaded";
import { findAmazonRequestIdForGraphQLError } from "../utils/findAmazonRequestIdForGraphQLError";

const BOOKING_ADD_AVAILABILITY_GQL = gql`
    mutation useBookingAddAvailability($bookingId: String!, $availabilityId: String!) {
        booking: bookingAddAvailability(input: { bookingSelector: { id: $bookingId }, id: $availabilityId }) {
            id
        }
    }
`;

const BOOKING_CREATE_GQL = gql`
    mutation useBookingCreate(
        $reference: String
        $leadPassengerName: String
        $autoFillQuestions: Boolean
        $consumerTrip: ConsumerTripInput
    ) {
        booking: bookingCreate(
            input: {
                reference: $reference
                leadPassengerName: $leadPassengerName
                autoFillQuestions: $autoFillQuestions
                consumerTrip: $consumerTrip
            }
        ) {
            id
            name
            state
            code
        }
    }
`;

type UseBookingAddAvailabilityResponse = {
    booking: {
        id: string;
    };
};

type BookingAddAvailabilityVariables = {
    bookingId: string;
    availabilityId: string;
};

type Booking = {
    id: string;
    name: string;
    state: string;
    code: string;
};

type BookingResponse = {
    booking: Booking;
};

type BookingCreateVariables = {
    reference?: string;
    leadPassengerName?: string;
    autoFillQuestions?: boolean;
    consumerTrip?: BookingConsumerTripInput;
};
type SimpleBooking = {
    id: string;
};

export type OnBookingCompletedCallback = (booking: SimpleBooking) => void;
export type UseCreateBookingOptions = {
    onBookingCompleted?: OnBookingCompletedCallback;
};
export const useCreateBooking = ({ onBookingCompleted }: UseCreateBookingOptions = {}) => {
    const [createBookingMutation, { loading: bookingCreateLoading, error: bookingCreateError }] = useMutation<
        BookingResponse,
        BookingCreateVariables
    >(BOOKING_CREATE_GQL);
    const [
        addBookingAvailabilityMutation,
        { loading: bookingAddAvailabilityLoading, error: bookingAddAvailabilityError },
    ] = useMutation<UseBookingAddAvailabilityResponse, BookingAddAvailabilityVariables>(BOOKING_ADD_AVAILABILITY_GQL);

    const createBooking = async ({
        availabilityId,
        reference,
        autoFillQuestions,
        consumerTrip,
    }: {
        availabilityId: string;
        reference?: string;
        autoFillQuestions?: boolean;
        consumerTrip?: BookingConsumerTripInput;
    }): Promise<SimpleBooking> => {
        const createBookingResult = await createBookingMutation({
            variables: { reference, autoFillQuestions, consumerTrip },
            onError: (error) => {
                const requestId = findAmazonRequestIdForGraphQLError(error);

                document.body.dispatchEvent(
                    new ConsumerApiError({
                        code: "CREATE_BOOKING",
                        availabilityId,
                        errorMessage: error.message,
                        requestId,
                    })
                );
            },
        });

        const addBookingAvailabilityResult = await addBookingAvailabilityMutation({
            variables: {
                bookingId: createBookingResult.data!.booking.id,
                availabilityId,
            },
            onCompleted: (data) => {
                if (onBookingCompleted) {
                    onBookingCompleted(data.booking);
                }
            },
            onError: (error) => {
                const requestId = findAmazonRequestIdForGraphQLError(error);

                document.body.dispatchEvent(
                    new ConsumerApiError({
                        code: "BOOKING_ADD_AVAILABILITY",
                        bookingId: createBookingResult.data!.booking.id,
                        availabilityId,
                        errorMessage: error.message,
                        requestId,
                    })
                );
            },
        });

        return addBookingAvailabilityResult.data!.booking;
    };

    return [
        createBooking,
        {
            loading: bookingCreateLoading || bookingAddAvailabilityLoading,
            error: bookingCreateError ?? bookingAddAvailabilityError,
        },
    ] as const;
};
